import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import AppBtn from "../../basics/button/AppBtn"
import { useRights } from "../../../hooks/rights"
import CouverturesCarte from "../couvertures/CouverturesCarte"
import SelecteurCibleModal from "../couvertures/SelecteurCibleModal"
import { showToast } from "../../../store/actions/toastActions"
import InputV2 from "../../basics/input/InputV2"
import SelectV2 from "../../basics/input/SelectV2"
import FilteredSelectV2 from "../../basics/input/FilteredSelectV2"
import PointInteretSelectV2, {
	POINTS_INTERETS_V2,
	POINTS_INTERETS_V2_ATELIER,
} from "../../others/PointInteretSelectV2"
import { COLORS } from "../../../utilities/Constants"
import {
	DEP_STATE_CANCELED,
	DEP_STATE_CHECK_DATA,
	DEP_STATE_READY_TO_DISPATCH,
	DEP_STATE_DISPATCHED,
	DEP_STATE_EN_SUSPENS,
	DEP_STATE_EN_ATTENTE,
	DEP_STATE_RENDEZ_VOUS,
	DEP_STATE_TRANSFERRED,
	DEP_TYPE_INTERVENTION,
	DEP_TYPE_LIST,
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_TYPE_APPEL_POLICE,
	DEPANNAGE_TYPE_APPEL_PRIVE,
	DEPANNAGE_TYPE_APPEL_CHOICES,
	typeNumber,
} from "../../../utilities/Addons"
import {
	getBaseDepMission,
	getBasePointInteret,
} from "../../../utilities/defaultObjects"
import { POINT_INTERET_TYPE_PARC } from "../../others/PointInteretComponent"
import {
	ASSURANCE_POLICE_ID,
	SECTEURS_POLICE,
	VEHICULE_CATEGORIE,
	VEHICULE_CATEGORIE_ORDRE,
} from "../../../utilities/Utils"
import {
	postDepMission,
	postDepMissionReset,
} from "../../../store/actions/depannageDispatchActions"
import {
	fetchAutreEntites,
	fetchVehModeles,
} from "../../../store/actions/autreActions"
import { fetchEmployes } from "../../../store/actions/depannageActions"
import { filterEmployesGroupe } from "../../../utilities/employes"
import { adresseDepuisEmail as adresseDepuisEmailLignes } from "../../../utilities/adresseDepuisEmail"
import { isPoValid } from "../../../utilities/poiValide"
import { fetchResumeMissions } from "../../../store/actions/assistancesActions"
import ChoixBulles from "../../basics/input/ChoixBulles"
import { HistoriqueRepliable } from "../../others/HistoriqueCardV2"
import ReponsesPrive, {
	reponsesPriveCompletes,
	reponsesPriveRequises as reponsesPriveRequisesPour,
} from "./ReponsesPrive"

// Formulaire V2 de création / édition d'une mission de dépannage (upsert
// api/depannages-dispatch via thunk postDepMission). Le type d'appel se choisit
// EN AMONT (boutons Assistance / Police / Privé du dispatch, ou Rdv agenda) —
// le modal ne le propose plus : sections Informations + Lieux sur une seule
// vue, chaînage clavier Enter, autosuggest marque/modèle (catalogue) et
// adresses (serveur).
// « Utiliser cette adresse » : POI pré-rempli depuis le lieu de l'e-mail
// (utilities/adresseDepuisEmail, partagé avec les propositions).
const adresseDepuisEmail = (dispatch, lignes) =>
	adresseDepuisEmailLignes(dispatch, lignes)

// Mise en forme d'envoi, appliquée AUSSI à la base du delta (revue phase B,
// 🟡 K) : destination inconnue → null explicite (le POST la garde NULL, la
// saisie du sélecteur masqué n'est pas envoyée) ; police → préfixe
// accident / clé dans l'info dépanneur.
const formaliser = (payload, destinationInconnue) => {
	if (destinationInconnue) payload.destination_finale = null
	if (payload.appel_id === ASSURANCE_POLICE_ID) {
		payload.info_depanneur = `${
			payload.accident_ou_enlevement === TYPE_ACCIDENT
				? "Accident"
				: "Enlèvement"
		}\n${
			payload.avec_cle === TYPE_AVEC_CLE ? "Avec Clé" : "Sans Clé"
		}\n${payload.info_depanneur}`
	}
	return payload
}

// GE-66 J4 : un privé n'a un total présumé (prix du bureau) que s'il est
// une livraison ; module-level (seuil rules-of-hooks de CRA 3).
// Passe 4 (🟡5) : sauf une relivraison privée (forfait de la grille).
const isPrivePresume = (dm) => !!dm.est_livraison && !dm.prive_relivraison

const DepMissionV2 = ({
	visible,
	closeModal,
	refreshDeps,
	editDepMission = null,
	// Type d'appel imposé à la création (DEPANNAGE_TYPE_APPEL_*) — fourni par
	// les boutons du dispatch. Ignoré quand editDepMission est passé.
	appelTypeInitial = null,
	// Callback optionnel appelé avec la mission créée/éditée (json serializé)
	// après le ✓ — utilisé par la concrétisation d'un Rdv agenda.
	onSaved = null,
	// Rappels des lieux saisis en texte libre sur le Rdv concrétisé :
	// affichés en pastille à côté des titres de section Lieux.
	rdvHints = null,
}) => {
	const dispatch = useDispatch()
	const { hasRight } = useRights()
	// Un POI jamais saisi arrive du serveur en type 0 — Parc
	// (PointInteret.get_base) : pour la saisie, on démarre sur Adresse. Un
	// vrai POI Parc sauvé a un id, il n'est pas touché.
	const estPoJamaisSaisi = (po) =>
		!po ||
		(po.id === null && !po.label && po.type === POINT_INTERET_TYPE_PARC)
	const poParDefaut = (po) =>
		estPoJamaisSaisi(po) ? getBasePointInteret() : po
	const [dm, setDm] = useState(() =>
		editDepMission
			? {
					...editDepMission,
					// Mission police = intervention : les missions nées avant
					// GE-16.3 (type Transport par défaut, sans sélecteur) sont
					// re-normalisées à l'ouverture.
					...(editDepMission.appel_type ===
					DEPANNAGE_TYPE_APPEL_POLICE
						? { type: DEP_TYPE_INTERVENTION }
						: {}),
					lieu_intervention: poParDefaut(
						editDepMission.lieu_intervention,
					),
					destination_finale: poParDefaut(
						editDepMission.destination_finale,
					),
				}
			: {
					...getBaseDepMission(),
					// GE-16.3 : police = intervention (destination
					// « Inconnue » possible, saisie terrain au bloc 7).
					// Lot « bulles » (Jordan 15.10) : assistance et privé
					// aussi en INTERVENTION par défaut (avant : transport) ;
					// tous deux gardent le choix du Type (privé : GE-50).
					...(appelTypeInitial !== null
						? {
								appel_type: appelTypeInitial,
								type: DEP_TYPE_INTERVENTION,
							}
						: {}),
				},
	)
	// Destination inconnue (décision Jordan 24.07.2026, étendue le 26.07 à
	// TOUS les types d'appel — assistance et privé compris, plus seulement
	// police/intervention) : la mission se sauve avec destination_finale =
	// null, la saisie devient obligatoire sur mobile
	// (destination_finale_valide reste false tant que t2 n'est pas posé). En
	// édition : une destination jamais saisie SANS texte d'import e-mail ne
	// peut venir que d'une sauvegarde « inconnue » (le POI vide n'est plus
	// créé côté serveur) — on re-sélectionne la pastille.
	const [destinationInconnue, setDestinationInconnue] = useState(
		() =>
			!!editDepMission &&
			estPoJamaisSaisi(editDepMission.destination_finale) &&
			!editDepMission.destination_finale_0,
	)
	const post = useSelector(
		(state) => state.depannageDispatchInfo.postDepMission,
	)
	const { appels } = useSelector((state) => state.depInfo)
	const marques = useSelector((state) => state.autreInfo.marques)
	const modeles = useSelector((state) => state.autreInfo.modeles)
	// Donneurs d'ordre = entités de type Assistance (nouveau référentiel,
	// remplace la liste legacy Appel). Les garages alimentent la pastille
	// « Garage » des sélecteurs de lieux.
	const assistances = useSelector((state) => state.autreInfo.assistances)
	const garages = useSelector((state) => state.autreInfo.garages)
	const employes = useSelector((state) => state.masterReducer.employes)

	const [selectedDepanneur, setSelectedDepanneur] = useState(() =>
		editDepMission &&
		editDepMission.etat === DEP_STATE_DISPATCHED &&
		editDepMission.depanneur
			? editDepMission.depanneur
			: 0,
	)

	useEffect(() => {
		if (
			!assistances ||
			assistances.length === 0 ||
			!garages ||
			garages.length === 0
		)
			dispatch(fetchAutreEntites())
		if (!employes || employes.length === 0) dispatch(fetchEmployes())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Marque du catalogue rattachée (null = frappe libre) → suggestions modèles.
	const [marqueId, setMarqueId] = useState(null)
	// « Utiliser cette adresse » : NPA à plusieurs localités → focus du NPA
	// (sa liste s'ouvre) dans PointInteretSelectV2.
	const [signalNpa, setSignalNpa] = useState(0)
	useEffect(() => {
		const m = (marques || []).find(
			(mq) =>
				mq.nom.toLowerCase() === (dm.marque || "").trim().toLowerCase(),
		)
		if (m) {
			setMarqueId(m.id)
			dispatch(fetchVehModeles(m.id))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Chaînage clavier.
	const typeRef = useRef(null)
	// GE-75 (revue 20.10, R8) : destination saisie avant « Relivrer ? Non »
	// (le dépôt la remplace), rendue sur « Oui ».
	const destAvantDepot = useRef(null)
	const appelRef = useRef(null)
	const numDossierRef = useRef(null)
	const assuranceRef = useRef(null)
	const nomRef = useRef(null)
	const prenomRef = useRef(null)
	const telephoneRef = useRef(null)
	const plaquesRef = useRef(null)
	const marqueRef = useRef(null)
	const modeleRef = useRef(null)
	const accidentRef = useRef(null)
	const secteurRef = useRef(null)
	const categorieRef = useRef(null)
	const diagRef = useRef(null)
	const infoDepanneurRef = useRef(null)
	const totalRef = useRef(null)

	const update = (key, value) => setDm((prev) => ({ ...prev, [key]: value }))
	const set = (key) => (e) => update(key, e.target.value)

	const onMarqueChange = (texte) => {
		update("marque", texte)
		if (marqueId) {
			setMarqueId(null)
			dispatch(fetchVehModeles(0))
		}
	}
	const onMarqueSelect = (marque) => {
		if (!marque) return
		update("marque", marque.nom)
		setMarqueId(marque.id)
		dispatch(fetchVehModeles(marque.id))
	}

	const updateLieuIntervention = (key, value, full = false) =>
		setDm((prev) => ({
			...prev,
			lieu_intervention: full
				? value
				: { ...prev.lieu_intervention, [key]: value },
		}))
	const updateDestinationFinale = (key, value, full = false) =>
		setDm((prev) => ({
			...prev,
			destination_finale: full
				? value
				: { ...prev.destination_finale, [key]: value },
		}))

	// --- Validation (identique au legacy ; privé = infos client/véhicule) ----
	// isPoValid : utilities/poiValide (partagé avec les propositions).
	const lieuCompleted =
		isPoValid(dm.lieu_intervention) &&
		(destinationInconnue || isPoValid(dm.destination_finale))
	const infoAssuranceCompleted =
		// Donneur d'ordre : entité Assistance (nouveau) OU appel legacy (les
		// missions existantes restent éditables sans re-sélection).
		(dm.assistance_entite > 0 || dm.appel_id !== 0) &&
		dm.num_dossier !== "" &&
		dm.nom !== "" &&
		dm.plaques !== "" &&
		dm.marque !== "" &&
		dm.modele !== ""
	const infoPoliceCompleted =
		dm.accident_ou_enlevement > 0 &&
		dm.avec_cle > 0 &&
		dm.secteur_police > 0 &&
		dm.vehicule_categorie > 0
	// GE-66 J4 (Jordan 13.10) : plus de total présumé pour un privé (le
	// tarif sort de la grille) ; seule une livraison garde le prix du bureau.
	const presumePrive = isPrivePresume(dm)
	// GE-66 (Jordan 10.10) + Q4 (15.10) : les 4 réponses du tarif privé
	// requises dans le FORMULAIRE (intervention et transport, pas une
	// livraison ni une mission existante).
	const reponsesPriveRequises = reponsesPriveRequisesPour(dm)
	const infoPriveCompleted =
		dm.nom !== "" &&
		dm.plaques !== "" &&
		dm.marque !== "" &&
		dm.modele !== "" &&
		(!reponsesPriveRequises || reponsesPriveCompletes(dm))

	const valide = useMemo(() => {
		if (dm.appel_type === DEPANNAGE_TYPE_APPEL_ASSISTANCE)
			return lieuCompleted && infoAssuranceCompleted
		if (dm.appel_type === DEPANNAGE_TYPE_APPEL_POLICE)
			return lieuCompleted && infoPoliceCompleted
		if (dm.appel_type === DEPANNAGE_TYPE_APPEL_PRIVE)
			return lieuCompleted && infoPriveCompleted
		return false
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dm, destinationInconnue])

	// --- Sauvegarde -----------------------------------------------------------
	const busy = post.saving || post.saved
	// Switch de confirmation du bouton « Annuler la mission ».
	const [confirmAnnulation, setConfirmAnnulation] = useState(false)
	// Mission renvoyée par le POST (pour onSaved).
	const savedMissionRef = useRef(null)
	// Revue 5 (🟠9) : depuis la décision 1a, le texte lieu_intervention_0..2
	// reste celui de la v1 ; une adresse plus récente attend dans une
	// proposition « à formaliser » — c'est ELLE que le formulaire montre et que
	// « Utiliser cette adresse » formalise.
	const [lieuAttente, setLieuAttente] = useState(null)
	useEffect(() => {
		if (!editDepMission || !editDepMission.id) return
		dispatch(fetchResumeMissions([editDepMission.id])).then((res) => {
			const r = res && res[editDepMission.id]
			const attente = r && r.adresse_en_attente
			if (attente && attente.lieu_intervention)
				setLieuAttente(attente.lieu_intervention)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const lignesLieuEmail = lieuAttente || [
		dm.lieu_intervention_0,
		dm.lieu_intervention_1,
		dm.lieu_intervention_2,
	]

	// GE-62 (B6) : état du formulaire à l'ouverture — référence du delta.
	const initialRef = useRef(dm)
	const initialDestInconnue = useRef(destinationInconnue)
	const creation = dm.id === null
	const willDispatch = creation
		? selectedDepanneur > 0
		: selectedDepanneur > 0 &&
			selectedDepanneur !== (editDepMission?.depanneur || 0)
	const save = () => {
		if (busy || !valide) return
		const payload = formaliser({ ...dm }, destinationInconnue)
		if (payload.etat === DEP_STATE_CHECK_DATA)
			payload.etat = DEP_STATE_READY_TO_DISPATCH
		if (willDispatch) {
			payload.etat = DEP_STATE_DISPATCHED
			payload.depanneur = selectedDepanneur
			payload.date_rendez_vous = null
			payload.delta_rendez_vous = 0
		}
		dispatch(postDepMission(JSON.stringify(corpsEdition(payload)))).then(
			(mission) => {
				if (mission) savedMissionRef.current = mission
			},
		)
	}

	// GE-62 (B6) : en édition, seuls les champs changés depuis l'ouverture
	// partent, avec la version lue (409 champ par champ si le dépanneur ou un
	// autre poste les a écrits entre-temps). Création : l'objet entier.
	const corpsEdition = (payload) => {
		if (creation || !editDepMission) return payload
		// Revue phase B (🟡 K) : la base passe par la MÊME mise en forme que
		// l'envoi (destination inconnue, préfixe police) — sinon ces champs
		// partaient à chaque enregistrement (refusés par la garde B7 sur une
		// mission passée à 30 entre-temps).
		const initial = formaliser(
			{ ...initialRef.current },
			initialDestInconnue.current,
		)
		const delta = { id: editDepMission.id }
		Object.keys(payload).forEach((k) => {
			if (JSON.stringify(payload[k]) !== JSON.stringify(initial[k]))
				delta[k] = payload[k]
		})
		// Normalisé à l'ouverture (police = intervention) : à écrire aussi.
		if (initial.type !== editDepMission.type) delta.type = payload.type
		delta.base_version = editDepMission.version || 0
		return delta
	}

	// Revue phase B (🟠5) : sur un 409, la liste est relue tout de suite —
	// rouvrir la mission repart de la version actuelle.
	useEffect(() => {
		if (post.error && post.error.httpStatus === 409 && refreshDeps)
			refreshDeps()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [post.error])

	// ✓ affiché 800 ms puis fermeture + refresh de la liste.
	useEffect(() => {
		if (!post.saved) return
		const timeout = setTimeout(() => {
			refreshDeps()
			if (onSaved && savedMissionRef.current)
				onSaved(savedMissionRef.current)
			dispatch(postDepMissionReset())
			closeModal()
		}, 800)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [post.saved])

	const close = () => {
		if (post.saving) return
		dispatch(postDepMissionReset())
		closeModal()
	}

	const isAssurance = dm.appel_type === DEPANNAGE_TYPE_APPEL_ASSISTANCE
	const isPolice = dm.appel_type === DEPANNAGE_TYPE_APPEL_POLICE
	const isPrive = dm.appel_type === DEPANNAGE_TYPE_APPEL_PRIVE
	// Sélecteur Type (Intervention / Transport) : assistance ET privé (GE-50).
	const champType = (
		<div style={s.field}>
			<span style={s.label}>Type</span>
			<ChoixBulles
				ref={typeRef}
				label="Type"
				niveau={1}
				// Intervention (le défaut) en premier.
				options={[...DEP_TYPE_LIST]
					.sort((a, b) => a.id - b.id)
					.map((t) => [t.id, t.label])}
				valeur={dm.type}
				onChange={(v) => update("type", v)}
				nextRef={isAssurance ? appelRef : nomRef}
			/>
		</div>
	)
	const typeChoisi = dm.appel_type !== -1
	// Libellé du type (posé en amont) — rappelé dans le titre du modal.
	const appelTypeLabel = (DEPANNAGE_TYPE_APPEL_CHOICES.find(
		(t) => t[0] === dm.appel_type,
	) || [])[1]

	// Annulation de la mission (édition uniquement) — convention modal : switch
	// de confirmation sur le bouton. Envoi partiel {id, etat} : l'upsert ne
	// touche que les clés fournies ; le backend nettoie le dispatch. Revue 5 :
	// avec la version lue (une mission terminée entre-temps → 409).
	const annulerMission = () => {
		if (busy) return
		if (!confirmAnnulation) {
			setConfirmAnnulation(true)
			return
		}
		dispatch(
			postDepMission(
				JSON.stringify({
					id: dm.id,
					etat: DEP_STATE_CANCELED,
					base_version: (editDepMission || dm).version || 0,
				}),
			),
		).then((mission) => {
			if (mission) savedMissionRef.current = mission
		})
	}

	// GE-60 (§5.1) : « C'est une couverture… » — mission importée qui n'est
	// qu'un engagement de paiement (ex. Mobi24 F1479610) ; les garde-fous
	// (démarrée, photos, encaissement) sont rendus par le serveur dans
	// l'aperçu du sélecteur.
	const [selecteurCouverture, setSelecteurCouverture] = useState(false)
	const peutRequalifier = hasRight("dep_dispatch")
	// Section Couvertures en LECTURE pour tous (agenda compris) ; les
	// actions restent réservées à `dep_dispatch` (contre-revue 08.10).
	const sectionCouvertures =
		!creation &&
		![DEP_STATE_CANCELED, DEP_STATE_TRANSFERRED].includes(
			editDepMission && editDepMission.etat,
		)
	const piedGauche = creation ? null : (
		<div style={s.piedGauche}>
			<AppBtn
				title={
					confirmAnnulation
						? "Confirmer l'annulation ?"
						: "Annuler la mission"
				}
				styleName="danger"
				onClick={annulerMission}
				disabled={busy}
			/>
			{sectionCouvertures && peutRequalifier && (
				<AppBtn
					title="C'est une couverture…"
					styleName="secondary"
					onClick={() => setSelecteurCouverture(true)}
					disabled={busy}
				/>
			)}
		</div>
	)

	const buttons = [
		{
			title: "Fermer",
			styleName: "secondary",
			onClick: close,
			disabled: busy,
		},
		{
			title: post.saving ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : post.saved ? (
				<FontAwesomeIcon icon={faCheck} />
			) : creation ? (
				selectedDepanneur > 0 ? (
					"Créer et dispatcher"
				) : (
					"Créer la mission"
				)
			) : willDispatch ? (
				"Enregistrer et dispatcher"
			) : (
				"Enregistrer"
			),
			styleName: "primary",
			onClick: save,
			disabled: !valide || busy,
		},
	]

	if (!visible) return null

	return (
		<CustomModal
			title={
				creation
					? `Nouvelle mission${appelTypeLabel ? ` · ${appelTypeLabel}` : ""}`
					: `Mission de dépannage${appelTypeLabel ? ` · ${appelTypeLabel}` : ""}`
			}
			subtitle={
				creation
					? "Saisir la demande puis l'envoyer au dispatch"
					: "Vérifier les données avant le dispatch"
			}
			onClose={close}
			showCloseButton={!busy}
			buttons={buttons}
			footerLeft={piedGauche}
			// Lot « bulles » (Jordan 15.10) : la rangée Destination la plus
			// longue (7 types + « Inconnue » = 711 px mesurés) tient sur UNE
			// ligne avec une barre de défilement de 17 px + 16 px de marge :
			// 711 + 17 + 16 + 48 (padding) = 792.
			maxWidth={792}
		>
			{typeChoisi && (
				<>
					{/* Première section du modal (le picker de type a disparu) :
					    pas de filet supérieur. */}
					<div style={{ ...s.sectionTitle, ...s.sectionTitleFirst }}>
						Informations
					</div>

					{/* --- Police : radios + secteur + catégorie ------------- */}
					{isPolice && (
						<>
							<div style={s.row2}>
								<div style={s.field}>
									<span style={s.label}>Clé</span>
									{/* Lot « bulles police » (Jordan 15.10,
									    option a) : Clé et nature en niveau 1
									    (rangée alignée) ; aucune présélection
									    (0 = non choisi, validation
									    inchangée). */}
									<ChoixBulles
										label="Clé"
										niveau={1}
										options={[
											[1, "Avec"],
											[2, "Sans"],
										]}
										valeur={dm.avec_cle || null}
										onChange={(v) => update("avec_cle", v)}
										nextRef={accidentRef}
									/>
								</div>
								<div style={s.field}>
									<span style={s.label}>
										Accident ou enlèvement
									</span>
									<ChoixBulles
										ref={accidentRef}
										label="Accident ou enlèvement"
										niveau={1}
										options={[
											[1, "Accident"],
											[2, "Enlèvement"],
										]}
										valeur={
											dm.accident_ou_enlevement || null
										}
										onChange={(v) =>
											update("accident_ou_enlevement", v)
										}
										nextRef={secteurRef}
									/>
								</div>
							</div>
							<div style={s.row2}>
								<div style={s.field}>
									<span style={s.label}>
										Lieu d'intervention police
									</span>
									<SelectV2
										ref={secteurRef}
										value={dm.secteur_police}
										onChange={(e) =>
											update(
												"secteur_police",
												parseInt(e.target.value),
											)
										}
										nextRef={categorieRef}
										style={s.input}
									>
										<option value={0}>-</option>
										{Object.keys(SECTEURS_POLICE).map(
											(key) => (
												<option
													key={`sp_${key}`}
													value={key}
												>
													{SECTEURS_POLICE[key]}
												</option>
											),
										)}
									</SelectV2>
								</div>
								<div style={s.field}>
									<span style={s.label}>
										Catégorie du véhicule
									</span>
									<SelectV2
										ref={categorieRef}
										value={dm.vehicule_categorie}
										onChange={(e) =>
											update(
												"vehicule_categorie",
												parseInt(e.target.value),
											)
										}
										nextRef={diagRef}
										style={s.input}
									>
										<option value={0}>-</option>
										{VEHICULE_CATEGORIE_ORDRE.map((key) => (
											<option
												key={`vc_${key}`}
												value={key}
											>
												{VEHICULE_CATEGORIE[key]}
											</option>
										))}
									</SelectV2>
								</div>
							</div>
						</>
					)}

					{/* --- Assurance / Privé : donneur d'ordre + client ------ */}
					{(isAssurance || isPrive) && (
						<>
							{/* GE-50 : un Privé peut être Intervention OU
							    Transport (défaut Transport), comme l'assistance. */}
							{isPrive && <div style={s.row3}>{champType}</div>}
							{isPrive && (
								<ReponsesPrive
									dm={dm}
									update={update}
									requis={reponsesPriveRequises}
									sansRdv={willDispatch}
									// GE-75 : relivraison Non la nuit → le
									// dépôt devient la destination finale.
									onDepotDestination={(depot) => {
										if (!destAvantDepot.current)
											destAvantDepot.current = {
												destination:
													dm.destination_finale,
												inconnue: destinationInconnue,
												depot: depot.id,
											}
										setDestinationInconnue(false)
										updateDestinationFinale(
											null,
											depot,
											true,
										)
									}}
									onRendreDestination={() => {
										const avant = destAvantDepot.current
										destAvantDepot.current = null
										// Rendue seulement si le dépôt est resté la
										// destination (pas retouchée à la main).
										if (
											!avant ||
											!dm.destination_finale ||
											dm.destination_finale.id !==
												avant.depot
										)
											return
										setDestinationInconnue(avant.inconnue)
										updateDestinationFinale(
											null,
											avant.destination,
											true,
										)
									}}
								/>
							)}
							{isAssurance && (
								<div style={s.row3}>
									{champType}
									<div style={s.field}>
										<span style={s.label}>Appel</span>
										<SelectV2
											ref={appelRef}
											value={dm.assistance_entite}
											onChange={(e) =>
												update(
													"assistance_entite",
													parseInt(e.target.value),
												)
											}
											nextRef={numDossierRef}
											style={s.input}
										>
											<option value={0}>-</option>
											{(assistances || []).map((a) => (
												<option
													key={`assistance_${a.id}`}
													value={a.id}
												>
													{a.label}
												</option>
											))}
										</SelectV2>
										{dm.assistance_entite === 0 &&
											dm.appel_id !== 0 && (
												<span style={s.hint}>
													Ancien référentiel :{" "}
													{(
														appels.find(
															(a) =>
																a.id ===
																dm.appel_id,
														) || {}
													).nom || "?"}{" "}
													— sélectionner une
													assistance pour migrer.
												</span>
											)}
									</div>
									<div style={s.field}>
										<span style={s.label}>N° dossier</span>
										<InputV2
											ref={numDossierRef}
											type="text"
											value={dm.num_dossier}
											onChange={set("num_dossier")}
											nextRef={assuranceRef}
											style={s.input}
										/>
									</div>
								</div>
							)}
							{isAssurance && (
								<div style={s.field}>
									<span style={s.label}>Assurance</span>
									<InputV2
										ref={assuranceRef}
										type="text"
										value={dm.assurance}
										onChange={set("assurance")}
										nextRef={nomRef}
										style={s.input}
									/>
								</div>
							)}
							<div style={s.row3}>
								<div style={s.field}>
									<span style={s.label}>Nom</span>
									<InputV2
										ref={nomRef}
										type="text"
										value={dm.nom}
										onChange={set("nom")}
										nextRef={prenomRef}
										style={s.input}
									/>
								</div>
								<div style={s.field}>
									<span style={s.label}>Prénom</span>
									<InputV2
										ref={prenomRef}
										type="text"
										value={dm.prenom}
										onChange={set("prenom")}
										nextRef={telephoneRef}
										style={s.input}
									/>
								</div>
								<div style={s.field}>
									<span style={s.label}>Téléphone</span>
									<InputV2
										ref={telephoneRef}
										type="tel"
										value={dm.telephone}
										onChange={set("telephone")}
										nextRef={plaquesRef}
										style={s.input}
									/>
								</div>
							</div>
							<div style={s.row3}>
								<div style={s.field}>
									<span style={s.label}>Plaques</span>
									<InputV2
										ref={plaquesRef}
										type="text"
										value={dm.plaques}
										onChange={set("plaques")}
										nextRef={marqueRef}
										style={s.input}
									/>
								</div>
								<div style={s.field}>
									<span style={s.label}>Marque</span>
									<FilteredSelectV2
										options={marques || []}
										defaultKey="nom"
										optionKeys="nom"
										value={dm.marque}
										onChange={onMarqueChange}
										onSelect={onMarqueSelect}
										inputRef={marqueRef}
										nextRef={modeleRef}
										placeholder="Marque"
										inputStyle={s.input}
									/>
								</div>
								<div style={s.field}>
									<span style={s.label}>Modèle</span>
									<FilteredSelectV2
										options={marqueId ? modeles || [] : []}
										defaultKey="nom"
										optionKeys="nom"
										value={dm.modele}
										onChange={(texte) =>
											update("modele", texte)
										}
										onSelect={(m) =>
											m && update("modele", m.nom)
										}
										inputRef={modeleRef}
										nextRef={diagRef}
										placeholder="Modèle"
										inputStyle={s.input}
									/>
								</div>
							</div>
						</>
					)}

					{/* --- Commun : diagnostic / info / total ---------------- */}
					<div style={s.row2}>
						<div style={s.field}>
							<span style={s.label}>Diagnostic présumé</span>
							<textarea
								ref={diagRef}
								value={dm.diagnostique_presume}
								onChange={set("diagnostique_presume")}
								rows={3}
								style={s.textarea}
							/>
						</div>
						<div style={s.field}>
							<span style={s.label}>Info pour dépanneur</span>
							<textarea
								ref={infoDepanneurRef}
								value={dm.info_depanneur}
								onChange={set("info_depanneur")}
								rows={3}
								style={s.textarea}
							/>
						</div>
					</div>
					{/* Police : pas de total présumé — le tarif sort de la
					    grille police au terrain (GE-10), rien à présumer. */}
					{/* Privé : plus de présumé (GE-66 J4), sauf une livraison
					    au prix du bureau, corrigeable jusqu'à la conversion. */}
					{!isPolice &&
						(!isPrive || presumePrive) &&
						(creation ||
							dm.etat === DEP_STATE_CHECK_DATA ||
							(isPrive && dm.etat < DEP_STATE_TRANSFERRED)) && (
							<div style={s.row3}>
								<div style={s.field}>
									<span style={s.label}>Total présumé</span>
									<InputV2
										ref={totalRef}
										type="text"
										value={dm.total_presume}
										onKeyDown={(e) => typeNumber(e)}
										onChange={set("total_presume")}
										style={s.input}
									/>
								</div>
							</div>
						)}

					{/* GE-56 : encaissement police forcé, « Client parti sans
					    payer » saisi sur le terrain — lecture seule. */}
					{!!(dm.motif_non_encaisse || "").trim() && (
						<div style={s.field}>
							<span style={s.label}>
								Non encaissé sur le terrain — motif
							</span>
							<InputV2
								type="text"
								value={dm.motif_non_encaisse}
								readOnly
								style={{ ...s.input, ...s.inputReadonly }}
							/>
						</div>
					)}

					{/* --- Lieux --------------------------------------------- */}
					<div style={s.sectionTitle}>
						{dm.type === DEP_TYPE_INTERVENTION
							? "Lieu d'intervention"
							: "Point de départ"}
						{rdvHints && rdvHints.lieu ? (
							<span style={s.hintPill} title="Saisi sur le Rdv">
								{rdvHints.lieu}
							</span>
						) : null}
					</div>
					{lignesLieuEmail.some(Boolean) ? (
						<div style={s.field}>
							<span style={s.label}>
								{lieuAttente
									? "Nouvelle adresse reçue par e-mail (à formaliser)"
									: "Adresse importée depuis e-mail"}
							</span>
							<div
								style={{
									display: "flex",
									gap: 8,
									alignItems: "center",
								}}
							>
								<InputV2
									type="text"
									value={lignesLieuEmail
										.filter(Boolean)
										.join(" ")}
									readOnly
									style={{
										...s.input,
										...s.inputReadonly,
										flex: 1,
									}}
								/>
								<button
									type="button"
									style={s.btnSmall}
									onClick={() =>
										adresseDepuisEmail(
											dispatch,
											lignesLieuEmail,
										).then(({ poi, plusieurs }) => {
											updateLieuIntervention(
												"",
												poi,
												true,
											)
											if (plusieurs)
												setSignalNpa((n) => n + 1)
										})
									}
								>
									Utiliser cette adresse
								</button>
							</div>
						</div>
					) : null}
					<PointInteretSelectV2
						pointInteret={dm.lieu_intervention}
						updatePointInteret={updateLieuIntervention}
						signalNpa={signalNpa}
						inputStyle={s.input}
					/>

					<div style={s.sectionTitle}>
						{dm.type === DEP_TYPE_INTERVENTION
							? "Destination"
							: "Point d'arrivée"}
						{rdvHints && rdvHints.destination ? (
							<span style={s.hintPill} title="Saisi sur le Rdv">
								{rdvHints.destination}
							</span>
						) : null}
					</div>
					<PointInteretSelectV2
						pointInteret={dm.destination_finale}
						updatePointInteret={updateDestinationFinale}
						list={
							dm.appel_type === DEPANNAGE_TYPE_APPEL_POLICE
								? POINTS_INTERETS_V2
								: POINTS_INTERETS_V2_ATELIER
						}
						inputStyle={s.input}
						inconnu={destinationInconnue}
						onInconnu={setDestinationInconnue}
					/>

					{(creation ||
						[
							DEP_STATE_CHECK_DATA,
							DEP_STATE_READY_TO_DISPATCH,
							DEP_STATE_EN_SUSPENS,
							DEP_STATE_EN_ATTENTE,
							DEP_STATE_RENDEZ_VOUS,
							DEP_STATE_DISPATCHED,
						].includes(dm.etat)) && (
						<>
							<div style={s.sectionTitle}>Dispatch</div>
							<div style={s.field}>
								<div style={s.dispatchHead}>
									<span style={s.label}>Dépanneur</span>
									{!creation &&
										editDepMission &&
										editDepMission.etat ===
											DEP_STATE_DISPATCHED &&
										editDepMission.depanneur > 0 && (
											<span style={s.badgeDispatched}>
												<span style={s.badgeDot} />
												Dispatchée à{" "}
												{(employes || []).find(
													(e) =>
														e.id ===
														editDepMission.depanneur,
												)?.nom ||
													`#${editDepMission.depanneur}`}
											</span>
										)}
								</div>
								<SelectV2
									value={selectedDepanneur}
									onChange={(e) =>
										setSelectedDepanneur(
											parseInt(e.target.value),
										)
									}
									style={s.input}
								>
									<option value={0}>
										Choisir un dépanneur…
									</option>
									{filterEmployesGroupe(
										employes,
										selectedDepanneur,
									).map((d) => (
										<option key={d.id} value={d.id}>
											{d.nom}
										</option>
									))}
								</SelectV2>
							</div>
						</>
					)}

					{/* GE-60 : couvertures de la mission avant conversion —
					    elles suivent jusqu'à la fiche. */}
					{sectionCouvertures && (
						<CouverturesCarte
							missionId={dm.id}
							compact
							sousTitre={[
								`Mission M${dm.id}`,
								appelTypeLabel,
								dm.plaques,
							]
								.filter(Boolean)
								.join(" · ")}
						/>
					)}

					{/* Notes système (Jordan 19.10) : boss seulement. */}
					{!creation && (
						<HistoriqueRepliable
							domaine="mission"
							objetId={dm.id}
							entete={
								<div style={s.sectionTitle}>Historique</div>
							}
						/>
					)}

					{post.error && (
						<div style={s.error}>
							{post.error.httpStatus === 409 ? (
								// GE-62 (B6) : modifié entre-temps. Revue 5 : le
								// message serveur nomme les champs (libellés) ; ici
								// seulement qui les a modifiés, sans clé ni id.
								<>
									{post.error.message}{" "}
									{[
										...new Set(
											Object.values(
												post.error.fields || {},
											).map((c) => c && c.message),
										),
									]
										.filter(Boolean)
										.join(" ")}{" "}
									Fermez et rouvrez la mission pour repartir
									de la version actuelle.
								</>
							) : (
								<>
									{post.error.message ||
										"La sauvegarde a échoué."}{" "}
									{(post.error.httpStatus >= 400 &&
										post.error.httpStatus < 500) ||
									post.error.httpStatus === 500
										? "Rien n'a été enregistré."
										: "La sauvegarde a peut-être abouti : rechargez la mission avant de réessayer."}
								</>
							)}
						</div>
					)}
				</>
			)}
			{selecteurCouverture && (
				<SelecteurCibleModal
					mode="couverture"
					missionId={dm.id}
					onClose={() => setSelecteurCouverture(false)}
					onDone={(res) => {
						setSelecteurCouverture(false)
						dispatch(
							showToast(
								"success",
								`Mission M${dm.id} convertie en couverture${
									res && res.fiche_id
										? ` de la fiche ${res.fiche_id}`
										: ""
								}`,
							),
						)
						refreshDeps()
						dispatch(postDepMissionReset())
						closeModal()
					}}
				/>
			)}
		</CustomModal>
	)
}

const s = {
	piedGauche: { display: "flex", gap: 10, flexWrap: "wrap" },
	field: { marginBottom: 14, display: "flex", flexDirection: "column" },
	row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
	row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 },
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		width: "100%",
		height: 38,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	inputReadonly: {
		backgroundColor: COLORS.bg,
		color: COLORS.textMuted,
	},
	textarea: {
		width: "100%",
		padding: "10px 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
		resize: "vertical",
	},
	sectionTitle: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		margin: "6px 0 12px",
		paddingTop: 12,
		borderTop: `1px solid ${COLORS.border}`,
	},
	sectionTitleFirst: { borderTop: "none", paddingTop: 0, marginTop: 0 },
	hint: {
		marginTop: 4,
		fontSize: 12,
		color: COLORS.textLight,
	},
	// Pastille de rappel (lieu/destination du Rdv) dans un sectionTitle
	// uppercase → on neutralise la casse et l'espacement du titre.
	hintPill: {
		display: "inline-flex",
		alignItems: "center",
		marginLeft: 10,
		padding: "2px 10px",
		borderRadius: 99,
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: "normal",
		textTransform: "none",
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
	error: {
		marginTop: 6,
		fontSize: 13,
		color: COLORS.danger,
	},
	btnSmall: {
		padding: "6px 14px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.accent,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
		whiteSpace: "nowrap",
	},
	dispatchHead: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 10,
	},
	badgeDispatched: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		fontSize: 12,
		fontWeight: 600,
		padding: "3px 10px",
		borderRadius: 99,
		backgroundColor: COLORS.successLight,
		color: COLORS.success,
	},
	badgeDot: {
		width: 7,
		height: 7,
		borderRadius: 99,
		backgroundColor: COLORS.success,
	},
}

export default DepMissionV2

// Ré-export depuis la source unique (utilities/Addons) pour compat des
// importateurs historiques (DepMissionCardV2, Agenda).
export {
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_TYPE_APPEL_POLICE,
	DEPANNAGE_TYPE_APPEL_PRIVE,
	DEPANNAGE_TYPE_APPEL_CHOICES,
} from "../../../utilities/Addons"
export const TYPE_ENLEVEMENT = 0
export const TYPE_ACCIDENT = 1
export const CLE = 0
export const TYPE_AVEC_CLE = 1
